/**
 * Real, linkable routes (`createBrowserRouter`).
 *
 * The guard is the interesting part. It reads the session through
 * `session.useSession()` — `useSyncExternalStore` underneath — so **clearing
 * the session actually re-renders**. A guard that reads `localStorage` during
 * render subscribes to nothing: the app would go on rendering while every
 * request 401s (SSO_INTEGRATION §3.4, the second of the three mistakes).
 */

import { SsoRedirect } from '@fiveox/shell'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'

import { App } from './App'
import { Callback } from './routes/Callback'
import { ClientApproval } from './routes/ClientApproval'
import { Calendar } from './routes/Calendar'
import { Clients } from './routes/Clients'
import { ClientLayout } from './routes/ClientLayout'
import { Activity } from './routes/Activity'
import { ClientProfile } from './routes/ClientProfile'
import { Intake } from './routes/Intake'
import { Overview } from './routes/Overview'
import { Download } from './routes/Download'
import { FiledCompare } from './routes/FiledCompare'
import { Filing } from './routes/Filing'
import { Gstr3b } from './routes/Gstr3b'
import { Periods } from './routes/Periods'
import { Reports } from './routes/Reports'
import { TbRecon } from './routes/TbRecon'
import { Recon } from './routes/Recon'
import { Review } from './routes/Review'
import { ScreenGate } from './routes/ScreenGate'
import { CLIENT_SCREENS } from './lib/clientScope'
import { RETURN_KEY, ssoConfig } from './lib/sso-config'
import { armSessionEnd } from './lib/session-end'
import { session } from './lib/session'

function RequireSession({ children }: { children: ReactNode }) {
  const current = session.useSession()

  // **A session ends everywhere it was trusted** (ADR-0024). Armed here because
  // this is the component that already owns the *no session -> SSO* decision,
  // and it is mounted exactly once: the watchdog that asks auth whether this
  // session is still live, the scoped sign-out listener, and the org clear.
  //
  // Nothing in it navigates. Ending the session re-renders this guard into
  // `SsoRedirect` below, which is the authoritative path.
  useEffect(armSessionEnd, [])

  // No session — hand the browser to accounts. **This app has no login screen**
  // (ADR-0048); `SsoRedirect` stashes the deep link and starts the PKCE flow.
  if (!current) {
    return <SsoRedirect config={ssoConfig()} returnKey={RETURN_KEY} />
  }
  return <>{children}</>
}

function Shell() {
  return (
    <RequireSession>
      <App>
        <Outlet />
      </App>
    </RequireSession>
  )
}

export const router = createBrowserRouter([
  // `/callback` sits **outside** the guard: it is the route that creates the
  // session, so guarding it would redirect the sign-in away from its own
  // completion.
  { path: '/callback', element: <Callback /> },
  // The client's approval page (FR-E15, B-09) — outside the guard for a different reason:
  // the client is not a user of this app and never signs in (ADR-0002). The token in the path
  // is the whole capability, checked by the service.
  { path: '/approve/:token', element: <ClientApproval /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Overview /> },
      { path: 'clients', element: <Clients /> },
      // Org-level (FR-E7): every client's due dates, behind the same permission its sidebar
      // item needs (`ORG_SCREEN_NEEDS`), from the same table and gate as the client screens.
      { path: 'calendar', element: <ScreenGate screen="calendar"><Calendar /></ScreenGate> },

      // ND-C1: URL scope. The id in the path is the source of truth for which
      // client a screen is about, so every screen is linkable, two clients can
      // be open in two tabs, and a screenshot says which client it is.
      // `ClientLayout` resolves the id once and answers "no such client"
      // itself, so no child screen renders an empty list for an id that is not
      // ours.
      {
        path: 'clients/:id',
        element: <ClientLayout />,
        children: [
          // Each screen behind the permission its sidebar item needs (N9) — one table,
          // `lib/screenAccess.ts`, for both.
          { index: true, element: <ScreenGate screen=""><ClientProfile /></ScreenGate> },
          { path: 'intake', element: <ScreenGate screen="intake"><Intake /></ScreenGate> },
          { path: 'review', element: <ScreenGate screen="review"><Review /></ScreenGate> },
          { path: 'returns', element: <ScreenGate screen="returns"><Periods /></ScreenGate> },
          { path: 'gstr3b', element: <ScreenGate screen="gstr3b"><Gstr3b /></ScreenGate> },
          {
            path: 'reconciliation',
            element: <ScreenGate screen="reconciliation"><Recon /></ScreenGate>,
          },
          {
            path: 'filed-compare',
            element: <ScreenGate screen="filed-compare"><FiledCompare /></ScreenGate>,
          },
          { path: 'tb-recon', element: <ScreenGate screen="tb-recon"><TbRecon /></ScreenGate> },
          { path: 'reports', element: <ScreenGate screen="reports"><Reports /></ScreenGate> },
          { path: 'download', element: <ScreenGate screen="download"><Download /></ScreenGate> },
          { path: 'activity', element: <ScreenGate screen="activity"><Activity /></ScreenGate> },
          { path: 'filing', element: <ScreenGate screen="filing"><Filing /></ScreenGate> },
        ],
      },

      // The flat paths these screens used to live at. A bookmark, a link in
      // our own docs, or a browser's autocomplete will still reach for them —
      // and they carry no client, so the only honest destination is the picker
      // (B-06: never a half-rendered screen reading an undefined id).
      // Derived from CLIENT_SCREENS rather than retyped, so a screen added to
      // one list cannot be forgotten in the other — a forgotten entry here is
      // a bookmark that 404s, which nobody reports and everybody works around.
      ...CLIENT_SCREENS.map((path) => ({
        path,
        element: <Navigate to="/clients" replace />,
      })),
    ],
  },
])
